import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { MAX_CLASS_QUESTIONS } from "../../utils/classQuestions";

const fieldClass =
  "w-full px-3 py-2 border border-border-light rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold bg-white";

const blankQuestion = () => ({
  id: "",
  label: "",
  type: "text",
  options_text: "",
  required: false,
});

/**
 * Edits a class's registration questions (form rows from questionsToForm).
 * Three is the most a class needs; the server refuses a fourth.
 */
const ClassQuestionsEditor = ({ value, onChange }) => {
  const rows = value || [];

  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="space-y-3">
      {rows.map((row, index) => {
        const base = `class-question-${index}`;
        return (
          <div
            key={row.id || base}
            className="p-3 rounded-lg border border-border-light bg-white space-y-3"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <label
                  htmlFor={`${base}-label`}
                  className="block text-xs font-semibold text-gray-600 font-manrope mb-1"
                >
                  Question text
                </label>
                <input
                  id={`${base}-label`}
                  type="text"
                  maxLength={200}
                  value={row.label}
                  onChange={(e) => update(index, { label: e.target.value })}
                  placeholder="e.g. Name to print on the jersey"
                  className={fieldClass}
                />
              </div>
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                aria-label={`Remove question ${index + 1}`}
                className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="sm:w-48">
                <label
                  htmlFor={`${base}-type`}
                  className="block text-xs font-semibold text-gray-600 font-manrope mb-1"
                >
                  Answer type
                </label>
                <select
                  id={`${base}-type`}
                  value={row.type}
                  onChange={(e) => update(index, { type: e.target.value })}
                  className={fieldClass}
                >
                  <option value="text">Free text</option>
                  <option value="select">Dropdown</option>
                </select>
              </div>
              <label className="flex items-center gap-2 font-manrope text-sm text-text-primary sm:pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(row.required)}
                  onChange={(e) =>
                    update(index, { required: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-[#173151] focus:ring-[#173151]"
                />
                Required
              </label>
            </div>

            {row.type === "select" && (
              <div>
                <label
                  htmlFor={`${base}-options`}
                  className="block text-xs font-semibold text-gray-600 font-manrope mb-1"
                >
                  Choices, separated by commas
                </label>
                <input
                  id={`${base}-options`}
                  type="text"
                  value={row.options_text}
                  onChange={(e) =>
                    update(index, { options_text: e.target.value })
                  }
                  placeholder="YS, YM, YL, AS, AM"
                  className={fieldClass}
                />
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => onChange([...rows, blankQuestion()])}
        disabled={rows.length >= MAX_CLASS_QUESTIONS}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold font-manrope rounded-lg border border-[#173151] text-[#173151] hover:bg-[#173151]/5 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" />
        Add question
      </button>
    </div>
  );
};

export default ClassQuestionsEditor;
